"use client";

import {useEffect, useState} from "react";
import {readMutationExecution} from "@repo/ui/gailgpt/mutation-execution";
import {outcomeNotice, resolveToolApprovalStatus} from "@repo/ui/gailgpt/tool-approval-status";
import {useToolApprovalDecisionState} from "@/contexts/chat/ToolApprovalDecisionContext";
import {hasProposalChanges, type MutationProposal} from "@/services/wingman/mutation-proposal";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";

/** One decision per approval: duplicate clicks, expired proposals and settled calls cannot submit. */
export function useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh}: {
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<void>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}) {
   const approval = call.approval;
   const {state: {pending, requestedFresh, error}, begin, update} = useToolApprovalDecisionState(approval?.id);
   const [now, setNow] = useState(Date.now);
   const settled = completed || approval?.approved !== undefined;
   const expiresAt = approval?.proposal ? Date.parse(approval.proposal.expiresAt) : undefined;
   useEffect(() => {
      if (expiresAt === undefined || settled) return;
      const timer = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timer);
   }, [expiresAt, settled]);

   const expired = expiresAt !== undefined && expiresAt <= now;
   const canRespond = !!approval && active && !settled && !disabled && !pending && !!onRespond;
   // A preview that could not be read must never fall back to approving unreviewed changes.
   const canApply = canRespond && !expired && !approval?.previewUnavailable &&
      (!approval?.proposal || hasProposalChanges(approval.proposal));
   const status = resolveToolApprovalStatus({approval, completed, active, result, pending: !!pending});
   // Without a decision handler this viewer does not own the conversation.
   const ownerOnly = !!approval && active && !settled && !onRespond;
   // What Galactus saved, which can differ from the proposal; null when the receipt omitted it.
   const saved = status === "completed" && approval?.proposal ? readMutationExecution(result, approval.proposal)?.saved ?? null : null;

   const respond = async (approved: boolean) => {
      if (!approval || !canRespond || !onRespond || (approved && (!canApply || (expiresAt !== undefined && expiresAt <= Date.now())))) return;
      if (!begin(approved ? "apply" : "discard")) return;
      try {
         await onRespond({id: approval.id, approved});
      } catch {
         update({error: true});
      } finally {
         update({pending: null});
      }
   };

   const canRequestFresh = !!approval?.proposal && !!onRespond && !!onRequestFresh && !disabled && !pending && !requestedFresh &&
      (!!outcomeNotice(status)?.requestFresh || (status === "denied" && expired) || (!settled && expired && active));
   const requestFresh = async () => {
      if (!canRequestFresh || !approval?.proposal || !onRequestFresh || !onRespond || !begin("fresh")) return;
      try {
         // Retire an expired pending approval before asking for a new snapshot.
         if (!settled) await onRespond({id: approval.id, approved: false});
         await onRequestFresh(approval.proposal);
         update({requestedFresh: true});
      } catch {
         update({error: true});
      } finally {
         update({pending: null});
      }
   };

   return {
      canRequestFresh, requestFresh, requestedFresh, ownerOnly, saved,
      status, settled, expired, error, pending, canRespond, canApply, respond,
      remainingMs: expiresAt === undefined ? undefined : Math.max(0, expiresAt - now),
   };
}
